import React, { useCallback, useEffect, useRef, useState } from 'react';
import CheckRow from './CheckRow';
import everpassLogo from '../../assets/everpassLogo.svg';
import suitcase from '../../assets/suitcase.svg';
import gamepad from '../../assets/gamepad.svg';
import { scrollIntoViewWithinContainer } from '../../epg/utils/shared/scrollIntoView';
import { getModelConfig } from '../../utils/models';
import './ReadinessBlock.scss';

const AUTO_DISMISS_MS = 60000;
const TICK_MS = 1000;

const NON_PASS_STATUSES = new Set(['fail', 'at_risk', 'skipped']);

const FALLBACK_FIXES = {
  fixes: [
    'Restart your router',
    'Restart the device',
    'Test if internet works on another device',
    'Connect via ethernet instead of Wi-Fi',
  ],
};

// Returns the "What to Try" fix config for the focused check.
// Branches on check.status and detailData per the product spec.
const STATIC_FIX_CONFIG = {
  connectionSpeed: {
    fixes: [
      'Restart your router',
      'Move closer to your Wi-Fi router',
      'Test if internet works on another device',
      'Connect via ethernet instead of Wi-Fi',
    ],
  },
  drmLicenseReachability: {
    fixes: [
      'Restart your router',
      'Restart the device',
      'Test if internet works on another device',
      'Contact your network administrator if this is a business/office network (firewall may be blocking DRM traffic)',
    ],
  },
  manifestCdnAccess: {
    fixes: [
      'Restart your router',
      'Restart the device',
      'Test if internet works on another device',
      'Add the Uplynk CDN domain to your firewall allowlist, or contact your IT/support team to do it',
    ],
  },
  socketReachability: {
    requiresItSupport: true,
    fixes: [
      'Restart your router',
      'Move closer to your Wi-Fi router',
      'Test if internet works on another device',
      'Connect via ethernet instead of Wi-Fi',
    ],
  },
};

function getConnectionQualityFixes(d) {
  // avgLatencyMs === null means too few samples succeeded → unstable connection
  if (d.avgLatencyMs === null) {
    return { fixes: ['Restart your router', 'Restart the device', 'Test if internet works on another device', 'Connect via ethernet instead of Wi-Fi'] };
  }
  // Latency measured but high → congestion-related
  return { fixes: ['Restart your router', 'Move closer to your Wi-Fi router', 'Pause other devices using the internet', 'Connect via ethernet instead of Wi-Fi'] };
}

function getPortDomainFixes(check) {
  if (check.status === 'at_risk') {
    return { requiresItSupport: true, fixes: ['Restart your router', 'Test if internet works on another device', 'Add the required domains to your firewall allowlist, or contact your IT/support team to do it'] };
  }
  return { requiresItSupport: true, fixes: ['Restart your router', 'Restart the device', 'Test if internet works on another device', 'Add the required domains to your firewall allowlist, or contact your IT/support team to do it'] };
}

function getFixConfig(check) {
  if (!check) return FALLBACK_FIXES;
  const d = check.detailData || {};
  if (check.id === 'connectionQuality') return getConnectionQualityFixes(d);
  if (check.id === 'portDomainReachability') return getPortDomainFixes(check);
  return STATIC_FIX_CONFIG[check.id] ?? FALLBACK_FIXES;
}

function getSupportEmail(inactiveState) {
  const tokens = `${inactiveState.email || ''} ${inactiveState.contact || ''}`.split(/\s+/);
  const email = tokens.find(t => t.includes('@') && t.includes('.'));
  return email || 'support@everpass.com';
}

/**
 * Full-screen overlay shown when boot checks finish At-Risk or Not-Ready.
 * Header: title + status badge + issue count + logo.
 * Left column: scrollable failing check cards (navigable via remote).
 * Right column: "What to Try" — fixes specific to the focused check, updates on navigation.
 * Auto-dismisses after 60. Capture-phase keydown beats EPG/norigin.
 */
export default function ReadinessBlock({ checks, overallStatus, onDismiss }) {
  const visibleChecks = checks.filter(c => NON_PASS_STATUSES.has(c.status));
  const CLOSE_IDX = visibleChecks.length;
  const itemCount = CLOSE_IDX + 1;

  const [secondsLeft, setSecondsLeft] = useState(Math.round(AUTO_DISMISS_MS / 1000));
  const dismissCalled = useRef(false);
  const rowRefs = useRef([]);

  const [focusIdx, setFocusIdx] = useState(0);
  const focusIdxRef = useRef(0);

  const dismiss = useCallback(() => {
    if (dismissCalled.current) return;
    dismissCalled.current = true;
    onDismiss();
  }, [onDismiss]);

  // Countdown ticker
  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft(s => s - 1);
    }, TICK_MS);
    return () => clearInterval(interval);
  }, []);

  // Auto-dismiss
  useEffect(() => {
    const timer = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [dismiss]);

  // Capture-phase keydown registered before EPG/norigin loads.
  // Same-phase same-target listeners fire in registration order, so our handler
  // runs first. stopImmediatePropagation prevents EPG from seeing the key.
  useEffect(() => {
    const handleKey = (e) => {
      if (!['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Return', 'Accept'].includes(e.key)) return;
      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        setFocusIdx(i => { focusIdxRef.current = Math.min(i + 1, itemCount - 1); return focusIdxRef.current; });
      } else if (e.key === 'ArrowUp') {
        setFocusIdx(i => { focusIdxRef.current = Math.max(i - 1, 0); return focusIdxRef.current; });
      } else if (focusIdxRef.current === itemCount - 1) {
        dismiss();
      }
    };
    window.addEventListener('keydown', handleKey, { capture: true });
    return () => window.removeEventListener('keydown', handleKey, { capture: true });
  }, [dismiss, itemCount]);

  // Scroll focused row into view when focusIdx moves between cards
  useEffect(() => {
    if (focusIdx < CLOSE_IDX && rowRefs.current[focusIdx]) {
      scrollIntoViewWithinContainer(rowRefs.current[focusIdx], 'RkBlock__list', 40);
    }
  }, [focusIdx, CLOSE_IDX]);

  const inactiveState = getModelConfig().inactiveState;
  const supportEmail = getSupportEmail(inactiveState);
  const isNotReady = overallStatus === 'Not-Ready';
  const statusMod = isNotReady ? 'RkBlock--not-ready' : 'RkBlock--at-risk';

  // When Close is focused keep showing the last navigated check's fixes.
  const activeCheckIdx = focusIdx < CLOSE_IDX ? focusIdx : Math.max(0, CLOSE_IDX - 1);
  const activeCheck = visibleChecks[activeCheckIdx];
  const activeFixConfig = getFixConfig(activeCheck);

  return (
    <div className={`RkBlock ${statusMod}`} role="alertdialog" aria-label="Streaming Diagnostic">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="RkBlock__header">
        <div className="RkBlock__header-meta">
          <p className="RkBlock__eyebrow">
            <img className="RkBlock__eyebrow-icon" src={suitcase} alt="" aria-hidden="true" />{' '}Streaming Diagnostic
          </p>
          <div className="RkBlock__header-badges">
            <span className="RkBlock__badge">
              {isNotReady ? 'Not Ready' : 'At Risk'}
            </span>
            <span className="RkBlock__issue-count">
              {visibleChecks.length} {visibleChecks.length === 1 ? 'Issue' : 'Issues'} Found
            </span>
          </div>
        </div>
        <img className="RkBlock__logo" src={everpassLogo} alt="EverPass" />
      </div>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <div className="RkBlock__body">

        {/* Left: scrollable failing check cards */}
        <div className="RkBlock__left">
          <ul className="RkBlock__list" id="RkBlock__list">
            {visibleChecks.map((check, i) => (
              <CheckRow
                key={check.id}
                ref={el => { rowRefs.current[i] = el; }}
                check={check}
                focused={focusIdx === i}
              />
            ))}
          </ul>
        </div>

        {/* Right: "What to Try" — updates to match the focused check */}
        <div className="RkBlock__right">
          <p className="RkBlock__fixes-heading">What to Try</p>

          {'requiresItSupport' in activeFixConfig && activeFixConfig.requiresItSupport && (
            <div className="RkBlock__it-notice" role="note">
              <span className="RkBlock__it-dot" aria-hidden="true" />
              <p className="RkBlock__it-text">
                Port / Domain Reachability requires IT Support.
                Please get in contact with the email address below.
              </p>
            </div>
          )}

          <ol className="RkBlock__fixes-list">
            {activeFixConfig.fixes.map((fix, i) => (
              <li key={fix}>{fix}</li>
            ))}
          </ol>

          <p className="RkBlock__still-failing">
            Still failing? Email us to <strong>{supportEmail}</strong>
          </p>

          <button type="button" className={`RkBlock__done${focusIdx === CLOSE_IDX ? ' RkBlock__done--focused' : ''}`} onClick={dismiss}>Close</button>

          <div className="RkBlock__panel-footer">
            <span className="RkBlock__select-hint" aria-hidden="true">
              <img className="RkBlock__select-icon" src={gamepad} alt="" />{' '}Select
            </span>
            <span className="RkBlock__countdown">Auto-closing in {secondsLeft}s</span>
          </div>
        </div>
      </div>
    </div>
  );
}
